export const formatUserFullName = ({
	firstName,
	lastName,
}: {
	firstName?: string | null;
	lastName?: string | null;
}): string => {
	return [firstName, lastName].filter(Boolean).join(' ') ?? '';
};

/**
 * The name fields every Person-like row carries. Structural on purpose: `Pick<Person, ...>`
 * and any narrower select are assignable, and shared stays free of a data-access import.
 */
export interface PersonNameParts {
	displayName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
}

/**
 * A person's name for display: their `displayName` when set, otherwise first + last.
 * The one resolution every PIV/representative/investor name goes through — entities carry
 * only a `displayName`, natural persons often only first/last.
 */
export const formatPersonDisplayName = (person: PersonNameParts): string =>
	person.displayName || formatUserFullName(person);

/**
 * Split a single full-name string into first (first token) and last (the rest).
 * The inverse of {@link formatUserFullName}, for the few places that only carry a
 * single name string (e.g. a witness's self-entered full name). Returns an empty
 * object for blank input and omits `lastName` when there is only one token.
 */
export const splitFullName = (
	fullName?: string | null
): { firstName?: string; lastName?: string } => {
	const trimmed = fullName?.trim();
	if (!trimmed) {
		return {};
	}

	const separatorIndex = trimmed.indexOf(' ');
	if (separatorIndex === -1) {
		return { firstName: trimmed };
	}

	return {
		firstName: trimmed.slice(0, separatorIndex),
		lastName: trimmed.slice(separatorIndex + 1).trim() || undefined,
	};
};
